import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats, downloadEvidencePack } from '../services/api';
import { 
  LayoutDashboard, FileText, GraduationCap, MessageSquare, 
  AlertTriangle, Users, Settings, LogOut, Menu, X, TrendingUp,
  CheckCircle2, Clock, AlertCircle, Layers, Sparkles, BarChart3, Map, Shield,
  ClipboardList, FileUp, ShieldCheck, Loader2
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer
} from 'recharts';

// ERIC Logo Component
function ERICLogo() {
  return (
    <div className="flex flex-col">
      <div className="font-outfit font-bold text-2xl tracking-tight">
        <span className="text-emerald-500">E</span>
        <span className="text-blue-600">R</span>
        <span className="text-blue-600">I</span>
        <span className="text-orange-500">C</span>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [downloadingPack, setDownloadingPack] = useState(false);

  const handleEvidencePack = async () => {
    setDownloadingPack(true);
    try {
      await downloadEvidencePack();
    } catch (error) {
      alert('Could not generate the evidence pack. Please try again.');
    } finally {
      setDownloadingPack(false);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getDashboardStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', active: true },
    { icon: Sparkles, label: 'AI Assessment', path: '/ai-assessment' },
    { icon: Layers, label: 'Scorecard', path: '/scorecard' },
    { icon: Map, label: 'Roadmap', path: '/roadmap' },
    { icon: FileText, label: 'Maturity Quiz', path: '/assessment' },
    { icon: GraduationCap, label: 'Training Hub', path: '/training' },
    { icon: AlertTriangle, label: 'Risk Reports', path: '/reports' },
    { icon: ClipboardList, label: 'Incident Log', path: '/incidents' },
    { icon: FileUp, label: 'Document Scoring', path: '/documents' },
    { icon: MessageSquare, label: 'AI Assistant', path: '/chat' },
    { icon: Users, label: 'Team', path: '/team' },
    { icon: Settings, label: 'Settings', path: '/settings' },
    ...(user?.role === 'admin' ? [{ icon: Shield, label: 'Admin', path: '/admin' }] : []),
  ];

  const maturityLevels = {
    1: { label: 'Stagnant', color: '#DC2626', description: 'Reactive approach, blind spots present' },
    2: { label: 'Searching', color: '#D97706', description: 'Pockets of good practice emerging' },
    3: { label: 'Striving', color: '#F59E0B', description: 'Improving efficiency and governance' },
    4: { label: 'Stretching', color: '#059669', description: 'Good vision, integrated responses' },
    5: { label: 'Sustaining', color: '#10B981', description: 'Performance-focused, agile leadership' },
  };

  const currentMaturity = stats?.maturity?.level || 1;
  const maturityInfo = maturityLevels[currentMaturity];

  const gaugeData = [
    { name: 'Readiness', value: stats?.readiness_score || 0, fill: '#2563EB' },
    { name: 'Reliability', value: stats?.reliability_score || 0, fill: '#059669' },
    { name: 'Resilience', value: stats?.resilience_score || 0, fill: '#D97706' },
  ];

  const categoryScores = stats?.maturity?.scores ? Object.entries(stats.maturity.scores).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    score: value,
    fill: value >= 4 ? '#059669' : value >= 3 ? '#D97706' : '#DC2626'
  })) : [];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <ERICLogo />
            <button 
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  item.active 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.district_name || 'Admin'}</p>
              </div>
              <button 
                onClick={logout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <button 
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          <div>
            <h1 className="font-outfit text-2xl font-semibold text-slate-900">Risk Intelligence Dashboard</h1>
            <p className="text-slate-500">Welcome back, {user?.name}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase tracking-wider">District</p>
            <p className="font-medium text-slate-900">{user?.district_name || 'N/A'}</p>
          </div>
        </header>

        {/* Dashboard content */}
        <div className="p-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-surface border border-border rounded-lg p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-slate-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6 animate-in">
              {/* Maturity Level Banner */}
              <div className="bg-surface border border-border rounded-lg p-6" data-testid="maturity-banner">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Current Maturity Level</p>
                    <div className="flex items-center gap-4">
                      <span 
                        className="text-4xl font-outfit font-bold"
                        style={{ color: maturityInfo.color }}
                      >
                        Level {currentMaturity}
                      </span>
                      <span className="text-xl font-outfit font-semibold text-primary">
                        {maturityInfo.label}
                      </span>
                    </div>
                    <p className="text-text-muted mt-1">{maturityInfo.description}</p>
                  </div>
                  <Link 
                    to="/assessment"
                    className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-primary-hover transition-all"
                    data-testid="take-assessment-btn"
                  >
                    <TrendingUp className="w-5 h-5" />
                    Take Assessment
                  </Link>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {/* Readiness */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="readiness-card">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Readiness Score</p>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-outfit font-bold text-blue-600">{stats?.readiness_score || 0}%</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Prevention & Preparedness</p>
                </div>

                {/* Reliability */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="reliability-card">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Reliability Score</p>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-outfit font-bold text-emerald-600">{Math.round(stats?.reliability_score || 0)}%</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Leadership & Metrics</p>
                </div>

                {/* Resilience */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="resilience-card">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Resilience Score</p>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-outfit font-bold text-amber-600">{stats?.resilience_score || 0}%</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Recovery & Response</p>
                </div>

                {/* Training Progress */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="training-progress-card">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Training Progress</p>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-outfit font-bold text-slate-900">
                      {stats?.training?.completed_modules || 0}/{stats?.training?.total_modules || 10}
                    </span>
                    <span className="text-slate-500 text-sm mb-1">modules</span>
                  </div>
                </div>
              </div>

              {/* Scorecard Link Banner */}
              {!stats?.has_scorecard && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6 flex items-center justify-between">
                  <div>
                    <h3 className="font-outfit text-lg font-semibold text-slate-900">Complete Your AI Assessment</h3>
                    <p className="text-slate-600 text-sm">The 3Rs scores above will be calculated from your Balanced Scorecard responses</p>
                  </div>
                  <Link 
                    to="/ai-assessment"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all flex items-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    Start AI Assessment
                  </Link>
                </div>
              )}

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 3Rs Gauge - Simple Bar Display */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="3r-chart">
                  <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-6">The 3Rs Overview</h3>
                  <div className="space-y-6">
                    {gaugeData.map((item) => (
                      <div key={item.name}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }}></div>
                            <span className="font-medium text-slate-700">{item.name}</span>
                          </div>
                          <span className="text-2xl font-outfit font-bold" style={{ color: item.fill }}>
                            {Math.round(item.value)}%
                          </span>
                        </div>
                        <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${Math.min(100, Math.max(0, item.value))}%`, 
                              backgroundColor: item.fill 
                            }}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {item.name === 'Readiness' && 'Prevention & Preparedness'}
                          {item.name === 'Reliability' && 'Leadership & Metrics'}
                          {item.name === 'Resilience' && 'Recovery & Response'}
                        </p>
                      </div>
                    ))}
                  </div>
                  {!stats?.has_scorecard && (
                    <p className="text-xs text-slate-400 mt-4 text-center italic">
                      Complete AI Assessment for accurate scores
                    </p>
                  )}
                </div>

                {/* Category Scores */}
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="category-scores-chart">
                  <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Maturity Category Scores</h3>
                  {categoryScores.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={categoryScores} layout="vertical">
                          <XAxis type="number" domain={[0, 5]} />
                          <YAxis type="category" dataKey="name" width={100} />
                          <Tooltip formatter={(value) => value.toFixed(2)} />
                          <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                            {categoryScores.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-slate-500">
                      <p>Complete Maturity Quiz to see category scores</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reports & Quick Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Risk Reports Summary */}
                <div className="lg:col-span-2 bg-surface border border-border rounded-lg p-6" data-testid="reports-summary">
                  <h3 className="font-outfit text-lg font-semibold text-primary mb-4">Risk Reports Overview</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                      <p className="text-2xl font-outfit font-bold text-primary">{stats?.reports?.total || 0}</p>
                      <p className="text-xs text-text-muted uppercase tracking-wider">Total Reports</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-risk-warning/10 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-risk-warning" />
                      </div>
                      <p className="text-2xl font-outfit font-bold text-risk-warning">{stats?.reports?.pending || 0}</p>
                      <p className="text-xs text-text-muted uppercase tracking-wider">Pending</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-risk-safe/10 flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-risk-safe" />
                      </div>
                      <p className="text-2xl font-outfit font-bold text-risk-safe">{stats?.reports?.resolved || 0}</p>
                      <p className="text-xs text-text-muted uppercase tracking-wider">Resolved</p>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-surface border border-border rounded-lg p-6" data-testid="quick-actions">
                  <h3 className="font-outfit text-lg font-semibold text-primary mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={handleEvidencePack}
                      disabled={downloadingPack}
                      className="w-full flex items-center gap-3 p-3 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-all disabled:opacity-60"
                      data-testid="evidence-pack-btn"
                    >
                      {downloadingPack ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5 text-emerald-400" />}
                      <span className="font-medium">{downloadingPack ? 'Building PDF...' : 'Insurance Evidence Pack'}</span>
                    </button>
                    <Link 
                      to="/incidents" 
                      className="flex items-center gap-3 p-3 rounded-md hover:bg-slate-50 transition-all border border-border"
                      data-testid="quick-log-incident"
                    >
                      <ClipboardList className="w-5 h-5 text-red-500" />
                      <span className="font-medium text-primary">Log an Incident</span>
                    </Link>
                    <Link 
                      to="/reports" 
                      className="flex items-center gap-3 p-3 rounded-md hover:bg-slate-50 transition-all border border-border"
                    >
                      <AlertCircle className="w-5 h-5 text-risk-warning" />
                      <span className="font-medium text-primary">Report a Risk</span>
                    </Link>
                    <Link 
                      to="/chat" 
                      className="flex items-center gap-3 p-3 rounded-md hover:bg-slate-50 transition-all border border-border"
                    >
                      <MessageSquare className="w-5 h-5 text-accent-blue" />
                      <span className="font-medium text-primary">Ask AI Assistant</span>
                    </Link>
                    <Link 
                      to="/training" 
                      className="flex items-center gap-3 p-3 rounded-md hover:bg-slate-50 transition-all border border-border"
                    >
                      <GraduationCap className="w-5 h-5 text-risk-safe" />
                      <span className="font-medium text-primary">Continue Training</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
